import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "../../Components/Alert";
import PageHeader from "../../Components/PageHeader";
import SortableFilterableTable, {
  DEFAULT_PAGE_SIZE,
} from "../../Components/SortableFilterableTable";
import OrganizationSyncToolbar from "../../Components/OrganizationSyncToolbar";
import PeopleProfileEditDrawer from "../PeopleProfileEditDrawer";
import { buildPeopleDirectoryColumns } from "../peopleDirectoryColumns";

const defaultFormOptions = { departments: [], roles: [] };

const SAVE_NOTICE_DISMISS_MS = 5000;

export default function PeopleIndex({
  rows: initialRows = [],
  viewer_can_edit_people: viewerCanEditPeople = false,
  viewer_can_edit_employment_fields: viewerCanEditEmploymentFields = false,
  viewer_can_access_onboarding_offboarding_portal:
    viewerCanAccessOnboardingOffboardingPortal = false,
  onboarding_offboarding_portal_url: onboardingOffboardingPortalUrl = null,
  form_options: formOptions = defaultFormOptions,
  organizationSync = null,
}) {
  const [rows, setRows] = useState(initialRows);
  const [editingPerson, setEditingPerson] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  useEffect(() => {
    if (!saveNotice) return undefined;
    const id = window.setTimeout(
      () => setSaveNotice(null),
      SAVE_NOTICE_DISMISS_MS,
    );
    return () => window.clearTimeout(id);
  }, [saveNotice]);

  const handleEditPerson = useCallback((person) => {
    setSaveNotice(null);
    setEditingPerson(person);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setEditingPerson(null);
  }, []);

  const handleSaved = useCallback((updatedRow) => {
    setRows((prev) =>
      prev.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)),
    );
    setSaveNotice("Person was updated.");
    setEditingPerson(null);
  }, []);

  const columns = useMemo(
    () =>
      buildPeopleDirectoryColumns({
        canEdit: viewerCanEditPeople,
        onEditPerson: handleEditPerson,
      }),
    [viewerCanEditPeople, handleEditPerson],
  );

  return (
    <>
      {saveNotice ? (
        <div className="mb-4" role="status" aria-live="polite">
          <Alert variant="success">{saveNotice}</Alert>
        </div>
      ) : null}

      <PageHeader
        title="People"
        subtitle="Active employees for your organization. Directory fields marked with sync will arrive with IdP integration."
        actions={
          viewerCanAccessOnboardingOffboardingPortal &&
          onboardingOffboardingPortalUrl ? (
            <a
              className="btn btn--primary"
              href={onboardingOffboardingPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              On/Offboard Request
            </a>
          ) : null
        }
      />

      <section className="card-section">
        <div className="card">
          <div className="card__header">
            <h2>Directory</h2>
            {organizationSync && (
              <OrganizationSyncToolbar organizationSync={organizationSync} />
            )}
          </div>
          <SortableFilterableTable
            data={rows}
            columns={columns}
            emptyMessage="No people found. Adjust filters or confirm directory sync."
            ariaLabel="Active employees"
            pageSize={DEFAULT_PAGE_SIZE}
          />
        </div>
      </section>

      <PeopleProfileEditDrawer
        open={Boolean(editingPerson)}
        person={editingPerson}
        formOptions={formOptions}
        canEditEmploymentFields={viewerCanEditEmploymentFields}
        onClose={handleCloseDrawer}
        onSaved={handleSaved}
      />
    </>
  );
}
